import React, { useEffect, useState, useRef } from "react";
import { bool, array, object, string, number } from "prop-types";
import { Link } from "@quintype/components";
import get from "lodash/get";
import { useSelector } from "react-redux";
import { STATIC_KEYWORDS } from "../constants";
import { getTemplateName } from "./utils";
import { getBreakingNews } from "../../api/utils";

import "./breaking-news.m.css";
import { getCategoryAndSubCategory } from "../ga-utils";

const MARQUEE_MIN_DURATION_SECONDS = 18;
const MARQUEE_MAX_DURATION_SECONDS = 90;
const MARQUEE_TARGET_SPEED_PX_PER_SECOND = 55;
const MARQUEE_AVERAGE_CHARACTER_WIDTH_PX = 9;
const MARQUEE_ITEM_SPACING_PX = 48;

const getBreakingNewsMarqueeDuration = (breakingNews) => {
  const estimatedContentWidth = breakingNews.reduce((width, story) => {
    const headline = get(story, ["headline"], "");
    return width + headline.length * MARQUEE_AVERAGE_CHARACTER_WIDTH_PX + MARQUEE_ITEM_SPACING_PX;
  }, 0);
  const duration = Math.ceil(estimatedContentWidth / MARQUEE_TARGET_SPEED_PX_PER_SECOND);

  return Math.min(Math.max(duration, MARQUEE_MIN_DURATION_SECONDS), MARQUEE_MAX_DURATION_SECONDS);
};

const renderBreakingNewsMarquee = (breakingNews, breakingNewsConfig, pageType, sketchesHost, config, fromLocation) => {
  const items = (iteration) =>
    breakingNews.map((story) => {
      const linkedStorySlug = get(story, ["metadata", "linked-story-slug"], null);
      const hasLinkedStory = Boolean(linkedStorySlug);
      const externalLink = hasLinkedStory && breakingNewsConfig.open_in_new_tab ? `/${linkedStorySlug}` : false;
      const linkLabel = `Read full story: ${story.headline}`;
      const linkUrl = hasLinkedStory ? `${sketchesHost}/${linkedStorySlug}` : null;
      const templateName =
        pageType === "story-page" ? getTemplateName(fromLocation?.["story-template"]) : getTemplateName(pageType);

      const handleBreakingNewsClick = (e) => {
        if (!hasLinkedStory) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        window.dataLayer = window.dataLayer || [];

        const categoryInfo = getCategoryAndSubCategory(window.location, pageType, config, fromLocation);
        window.dataLayer.push({
          event: "dl_breaking_news_click",
          link_url: linkUrl,
          link_text: story.headline,
          category: categoryInfo?.category,
          sub_category: categoryInfo?.subCategory,
          content_id: categoryInfo?.contentId,
          template_name: templateName,
          platform: window.innerWidth >= 768 ? "desktop" : "mobile",
        });

        // Navigate to the linked story URL after tracking
        if (linkedStorySlug) {
          const fullUrl = externalLink ? linkUrl : `/${linkedStorySlug}`;
          window.location.href = fullUrl;
        }
      };

      return (
        <div key={`${story.id}-${iteration}`} onClick={handleBreakingNewsClick} styleName="item">
          {hasLinkedStory ? (
            <Link
              aria-label={linkLabel}
              styleName="item"
              href={`/${linkedStorySlug}`}
              externalLink={externalLink}
              disableAjaxLinks={true}
            >
              {story.headline}
            </Link>
          ) : (
            <span aria-label={story.headline} styleName="item">
              {story.headline}
            </span>
          )}
        </div>
      );
    });

  const finalItems = [...items(1), ...items(2)];
  const marqueeDuration = getBreakingNewsMarqueeDuration(breakingNews);

  return (
    <div styleName="marquee-wrapper" style={{ "--animation-speed": `${marqueeDuration}s` }}>
      <div styleName="marquee-container">{finalItems}</div>
    </div>
  );
};

export const BreakingNewsView = ({ updateInterval }) => {
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const breakingNewsStories = get(qtState, ["data", "breakingNewsStories"], []);
  const [breakingNewsList, setBreakingNewsList] = useState(breakingNewsStories);
  const pageType = get(qtState, ["pageType"], null);
  const breakingNewsConfig = get(qtState, ["config", "publisher-attributes", "breaking_news"], {});
  const config = get(qtState, ["config"], "");
  const sketchesHost = config?.["sketches-host"];
  const { BREAKING_NEWS_LABEL } = STATIC_KEYWORDS;
  const isBreakingNewsDisabled = breakingNewsList.length === 0 || !breakingNewsConfig.pages.includes(pageType);
  const breakingNewsCount = breakingNewsConfig.item_display;
  const fromLocation = useSelector((state) => get(state, ["qt", "data", "story"], {}));
  const isWebView = get(qtState, ["config", "isWebView"], false) === true;

  const fetchBreakingNews = async () => {
    try {
      const response = await getBreakingNews();

      // Preserve the current list on transient fetch failures; only replace it with a valid API payload.
      if (Array.isArray(response)) {
        setBreakingNewsList(response);
      }
    } catch (error) {
      console.error("Error fetching breaking news:", error);
    }
  };

  useEffect(() => {
    if (!isWebView) {
      fetchBreakingNews();

      const pollInterval = setInterval(() => {
        fetchBreakingNews();
      }, updateInterval);

      return () => {
        clearInterval(pollInterval);
      };
    }
  }, [isWebView, updateInterval]);

  useEffect(() => {
    if (breakingNewsStories && breakingNewsStories.length > 0) {
      setBreakingNewsList(breakingNewsStories);
    }
  }, [breakingNewsStories]);

  const emptyDivRef = useRef(null);

  useEffect(() => {
    if (isBreakingNewsDisabled && emptyDivRef.current) {
      const parentElement = emptyDivRef.current.parentElement;
      if (parentElement) {
        parentElement.style.minHeight = "0px";
      }
    }
  }, [isBreakingNewsDisabled]);

  if (isBreakingNewsDisabled) {
    return <div ref={emptyDivRef} className="empty-div-margin-bottom"></div>;
  }
  return (
    <div styleName="base">
      <div styleName="container">
        <div styleName="breaking-news-label">{BREAKING_NEWS_LABEL}</div>
        {renderBreakingNewsMarquee(
          breakingNewsList.slice(0, breakingNewsCount),
          breakingNewsConfig,
          pageType,
          sketchesHost,
          config,
          fromLocation
        )}
      </div>
    </div>
  );
};

BreakingNewsView.propTypes = {
  breakingNewsLoaded: bool,
  breakingNews: array,
  breakingNewsConfig: object,
  pageType: string,
  updateInterval: number,
};

export default BreakingNewsView;
